import { DPoPManager } from './DPoPManager';
import { TokenManager } from './TokenManager';

class RequestQueue {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.failedQueue = [];
  }

  async waitForRefresh() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    return Promise.resolve(true);
  }

  setRefreshPromise(promise) {
    this.refreshPromise = promise;
    return promise.finally(() => {
      this.refreshPromise = null;
      this.isRefreshing = false;
    });
  }

  startRefresh() {
    this.isRefreshing = true;
  }

  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  reset() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.failedQueue = [];
  }
}

class ToastManager {
  constructor() {
    this.pendingToasts = new Map();
    this.DEBOUNCE_TIME = 300;
  }

  showError(message, toastFunction) {
    if (!toastFunction) return;
    
    if (this.pendingToasts.has(message)) {
      clearTimeout(this.pendingToasts.get(message));
    }

    const timeoutId = setTimeout(() => {
      toastFunction(message);
      this.pendingToasts.delete(message);
    }, this.DEBOUNCE_TIME);

    this.pendingToasts.set(message, timeoutId);
  }

  showSuccess(message, toastFunction) {
    if (!toastFunction) return;
    
    if (this.pendingToasts.has(message)) {
      clearTimeout(this.pendingToasts.get(message));
    }

    const timeoutId = setTimeout(() => {
      toastFunction(message);
      this.pendingToasts.delete(message);
    }, this.DEBOUNCE_TIME);

    this.pendingToasts.set(message, timeoutId);
  }

  clear() {
    this.pendingToasts.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingToasts.clear();
  }
}

export class DPoPAPIService {
  constructor(baseURL, useSessionStorage = false) {
    this.baseURL = baseURL;
    this.dpopManager = new DPoPManager();
    this.tokenManager = new TokenManager(useSessionStorage);
    this.requestQueue = new RequestQueue();
    this.toastManager = new ToastManager();
    
    this.MAX_RETRIES = 2;
    this.RETRY_DELAY = 1000;
    this.RETRY_BACKOFF = 2;
    
    this.activeRequests = new Map();
    
    this.consecutiveRefreshFailures = 0;
    this.MAX_CONSECUTIVE_REFRESH_FAILURES = 2;
    this.lastRefreshAttempt = 0;
    this.MIN_REFRESH_INTERVAL = 1000;
    
    this.toastError = null;
    this.toastSuccess = null;
    
    this.isSessionValid = true;
    this.onSessionExpired = null;
  }

  setToastHandlers(errorHandler, successHandler) {
    this.toastError = errorHandler;
    this.toastSuccess = successHandler;
  }

  setSessionExpiredCallback(callback) {
    this.onSessionExpired = callback;
  }

  async initialize() {
    try {
      await this.dpopManager.initialize();
    } catch (error) {
      console.error('DPoP initialization failed:', error);
      throw error;
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getRetryDelay(attemptNumber) {
    return this.RETRY_DELAY * Math.pow(this.RETRY_BACKOFF, attemptNumber - 1);
  }

  async login(username, password, captchaToken) {
    const url = `${this.baseURL}/dpop_auth/token/`;
    
    try {
      const dpopProof = await this.dpopManager.generateDPoPProof('POST', url);

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'DPoP': dpopProof
        },
        body: JSON.stringify({ username, password, captcha_token: captchaToken })
      });

      if (!response.ok) {
        const errorData = await this._parseResponse(response);
        const errorMessage = errorData.message?.[0] || errorData.detail || 'Login failed';
        const error = new Error(errorMessage);
        error.response = { data: errorData, status: response.status };
        throw error;
      }

      const data = await this._parseResponse(response);
      
      this.tokenManager.setTokens(data.data.token.access, data.data.token.refresh);
      
      this.consecutiveRefreshFailures = 0;
      this.isSessionValid = true;
      this.requestQueue.reset();
      
      if (this.toastSuccess) {
        this.toastManager.showSuccess('Login successful', this.toastSuccess);
      }
      
      return data;
      
    } catch (error) {
      if (this.toastError && !error.response) {
        this.toastManager.showError(error.message, this.toastError);
      }
      throw error;
    }
  }

  async getSSOSession() {
    const url = `${this.baseURL}/dpop_auth/session/`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      return { authenticated: false, user: null };
    }

    return this._parseResponse(response);
  }

  async bootstrapFromSSO() {
    const url = `${this.baseURL}/dpop_auth/session/token/`;

    if (!this.dpopManager.isInitialized()) {
      await this.dpopManager.initialize();
    }

    const dpopProof = await this.dpopManager.generateDPoPProof('POST', url);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'DPoP': dpopProof
      }
    });

    if (!response.ok) {
      return null;
    }

    const data = await this._parseResponse(response);
    if (data?.data?.token?.access && data?.data?.token?.refresh) {
      this.tokenManager.setTokens(data.data.token.access, data.data.token.refresh);
      if (this.tokenManager.useSessionStorage) {
        sessionStorage.setItem('user', JSON.stringify(data.data));
      }
      this.consecutiveRefreshFailures = 0;
      this.isSessionValid = true;
      this.requestQueue.reset();
      return data;
    }

    return null;
  }

  async refreshAccessToken() {
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.MIN_REFRESH_INTERVAL) {
      await this.sleep(this.MIN_REFRESH_INTERVAL);
    }
    this.lastRefreshAttempt = now;

    if (this.requestQueue.isCurrentlyRefreshing()) {
      const result = await this.requestQueue.waitForRefresh();
      return result;
    }

    if (this.consecutiveRefreshFailures >= this.MAX_CONSECUTIVE_REFRESH_FAILURES) {
      this.handleSessionExpired();
      throw new Error('Session expired');
    }

    this.requestQueue.startRefresh();
    
    const url = `${this.baseURL}/dpop_auth/token/refresh/`;
    
    const refreshPromise = (async () => {
      try {
        const refreshToken = this.tokenManager.getRefreshToken();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        const dpopProof = await this.dpopManager.generateDPoPProof('POST', url);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'DPoP': dpopProof
          },
          body: JSON.stringify({ refresh: refreshToken })
        });

        if (!response.ok) {
          const errorData = await this._parseResponse(response).catch(() => ({}));
          const errorMessage = errorData.detail || errorData.message || 'Token refresh failed';
          
          this.consecutiveRefreshFailures++;
          
          if (this.consecutiveRefreshFailures >= this.MAX_CONSECUTIVE_REFRESH_FAILURES) {
            this.handleSessionExpired();
          }
          
          throw new Error(errorMessage);
        }

        const data = await this._parseResponse(response);
        
        this.tokenManager.updateAccessToken(data.data.access);
        
        this.consecutiveRefreshFailures = 0;
        this.isSessionValid = true;
        
        return true;
        
      } catch (error) {
        throw error;
      }
    })();

    return this.requestQueue.setRefreshPromise(refreshPromise);
  }

  handleSessionExpired() {
    if (!this.isSessionValid) {
      return;
    }
    
    this.isSessionValid = false;
    
    this.tokenManager.clearTokens();
    this.dpopManager.clearKeys();
    this.requestQueue.reset();
    this.activeRequests.clear();
    
    if (this.toastError) {
      this.toastManager.showError('Session expired. Please login again.', this.toastError);
    }

    if (this.onSessionExpired) {
      this.onSessionExpired();
    }
  }

  async authenticatedRequest(method, endpoint, body = null, extraHeaders = {}, options = {}) {
    if (!this.isSessionValid) {
      throw new Error('Session expired');
    }

    if (!this.dpopManager.isInitialized()) {
      let retries = 0;
      while (!this.dpopManager.isInitialized() && retries < 50) {
        await this.sleep(100);
        retries++;
      }
      if (!this.dpopManager.isInitialized()) {
        throw new Error('DPoP initialization timeout');
      }
    }

    const { showToast = true, retryCount = 0, maxRetries = this.MAX_RETRIES } = options;

    const requestKey = `${method}:${endpoint}:${JSON.stringify(body)}`;
    if (this.activeRequests.has(requestKey)) {
      return this.activeRequests.get(requestKey);
    }

    const requestPromise = this._executeRequest(
      method,
      endpoint,
      body,
      extraHeaders,
      showToast,
      retryCount,
      maxRetries
    );

    this.activeRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.activeRequests.delete(requestKey);
    }
  }

  async downloadFile(method, endpoint, body = null, extraHeaders = {}, options = {}) {
    if (!this.isSessionValid) {
      throw new Error('Session expired');
    }

    if (!this.dpopManager.isInitialized()) {
      let retries = 0;
      while (!this.dpopManager.isInitialized() && retries < 50) {
        await this.sleep(100);
        retries++;
      }
      if (!this.dpopManager.isInitialized()) {
        throw new Error('DPoP initialization timeout');
      }
    }

    const { showToast = true, retryCount = 0, maxRetries = this.MAX_RETRIES } = options;

    try {
      let accessToken = this.tokenManager.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      let dpopProof = await this.dpopManager.generateDPoPProof(method, endpoint, accessToken);

      const fetchOptions = {
        method,
        headers: {
          'Authorization': `DPoP ${accessToken}`,
          'DPoP': dpopProof,
          ...extraHeaders
        }
      };

      if (body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        fetchOptions.headers['Content-Type'] = 'application/json';
        fetchOptions.body = JSON.stringify(body);
      }

      let response = await fetch(endpoint, fetchOptions);

      if (await this._shouldRefreshResponse(response)) {
        try {
          await this.refreshAccessToken();
          
          accessToken = this.tokenManager.getAccessToken();
          
          if (!accessToken) {
            throw new Error('No access token after refresh');
          }
          
          dpopProof = await this.dpopManager.generateDPoPProof(method, endpoint, accessToken);
          
          const retryOptions = {
            method,
            headers: {
              'Authorization': `DPoP ${accessToken}`,
              'DPoP': dpopProof,
              ...extraHeaders
            }
          };

          if (body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
            retryOptions.headers['Content-Type'] = 'application/json';
            retryOptions.body = JSON.stringify(body);
          }

          response = await fetch(endpoint, retryOptions);
          
        } catch (refreshError) {
          throw new Error('Session expired');
        }
      }

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || `Download failed: ${response.statusText}`;
        } catch {
          errorMessage = `Download failed: ${response.statusText}`;
        }
        
        if (retryCount < maxRetries && this._isRetryableError(response.status)) {
          const delay = this.getRetryDelay(retryCount + 1);
          
          await this.sleep(delay);
          
          return this.downloadFile(method, endpoint, body, extraHeaders, {
            showToast,
            retryCount: retryCount + 1,
            maxRetries
          });
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      return await response.arrayBuffer();
      
    } catch (error) {
      if (retryCount < maxRetries && error.name === 'TypeError') {
        const delay = this.getRetryDelay(retryCount + 1);
        
        await this.sleep(delay);
        
        return this.downloadFile(method, endpoint, body, extraHeaders, {
          showToast,
          retryCount: retryCount + 1,
          maxRetries
        });
      }
      
      if (showToast && this.toastError && error.message !== 'Session expired') {
        this.toastManager.showError(error.message, this.toastError);
      }
      
      throw error;
    }
  }

  async _executeRequest(method, endpoint, body, extraHeaders, showToast, retryCount, maxRetries) {
    try {
      let accessToken = this.tokenManager.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      let dpopProof = await this.dpopManager.generateDPoPProof(method, endpoint, accessToken);

      const fetchOptions = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `DPoP ${accessToken}`,
          'DPoP': dpopProof,
          ...extraHeaders
        }
      };

      if (body) {
        fetchOptions.body = JSON.stringify(body);
      }

      let response = await fetch(endpoint, fetchOptions);

      if (await this._shouldRefreshResponse(response)) {
        try {
          await this.refreshAccessToken();
          
          accessToken = this.tokenManager.getAccessToken();
          
          if (!accessToken) {
            throw new Error('No access token after refresh');
          }
          
          dpopProof = await this.dpopManager.generateDPoPProof(method, endpoint, accessToken);
          
          const retryOptions = {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `DPoP ${accessToken}`,
              'DPoP': dpopProof,
              ...extraHeaders
            }
          };

          if (body) {
            retryOptions.body = JSON.stringify(body);
          }

          response = await fetch(endpoint, retryOptions);
          
        } catch (refreshError) {
          throw new Error('Session expired');
        }
      }

      if (!response.ok) {
        const errorData = await this._parseResponse(response).catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Request failed: ${response.statusText}`;
        
        if (retryCount < maxRetries && this._isRetryableError(response.status)) {
          const delay = this.getRetryDelay(retryCount + 1);
          
          await this.sleep(delay);
          
          return this.authenticatedRequest(method, endpoint, body, extraHeaders, {
            showToast,
            retryCount: retryCount + 1,
            maxRetries
          });
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      return this._parseResponse(response);
      
    } catch (error) {
      if (retryCount < maxRetries && error.name === 'TypeError') {
        const delay = this.getRetryDelay(retryCount + 1);
        
        await this.sleep(delay);
        
        return this.authenticatedRequest(method, endpoint, body, extraHeaders, {
          showToast,
          retryCount: retryCount + 1,
          maxRetries
        });
      }
      
      if (showToast && this.toastError && error.message !== 'Session expired') {
        this.toastManager.showError(error.message, this.toastError);
      }
      
      throw error;
    }
  }

  async _parseResponse(response) {
    const contentType = response.headers.get('content-type');
    
    try {
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      
      const text = await response.text();
      
      if (text.startsWith('{') || text.startsWith('[')) {
        return JSON.parse(text);
      }
      
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
        throw new Error('Received HTML instead of JSON - check API endpoint');
      }
      
      return { data: text };
      
    } catch (error) {
      throw error;
    }
  }

  _isRetryableError(statusCode) {
    return [408, 429, 500, 502, 503, 504].includes(statusCode);
  }

  _errorDataText(errorData = {}) {
    const values = [
      errorData.detail,
      errorData.error,
      errorData.message,
      errorData.messages,
      errorData.code
    ];

    return values
      .flatMap(value => Array.isArray(value) ? value : [value])
      .filter(Boolean)
      .map(value => typeof value === 'string' ? value : JSON.stringify(value))
      .join(' ')
      .toLowerCase();
  }

  async _shouldRefreshResponse(response) {
    if (response.status === 401) {
      return true;
    }

    if (response.status !== 403) {
      return false;
    }

    const errorData = await this._parseResponse(response.clone()).catch(() => ({}));
    const errorText = this._errorDataText(errorData);

    return (
      errorText.includes('token is expired') ||
      errorText.includes('token has expired') ||
      errorText.includes('token expired') ||
      errorText.includes('token_not_valid') ||
      errorText.includes('given token not valid')
    );
  }

  logout() {
    this.tokenManager.clearTokens();
    this.dpopManager.clearKeys();
    this.requestQueue.reset();
    this.activeRequests.clear();
    this.toastManager.clear();
    this.consecutiveRefreshFailures = 0;
    this.isSessionValid = true;
  }

  async logoutSSO(historyId = null) {
    try {
      await fetch(`${this.baseURL}/dpop_auth/session/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: historyId ? JSON.stringify({ history_id: historyId }) : undefined
      });
    } catch (error) {
      // Local logout should still complete when the shared session call fails.
    } finally {
      this.logout();
    }
  }

  isAuthenticated() {
    return this.tokenManager.hasTokens() && this.dpopManager.isInitialized() && this.isSessionValid;
  }
}

export { ToastManager };

